// Wording for the GRC readiness modal: what the indicator was measured against, and whether a
// percentage may be shown at all.
//
// Three bases, from FrameworkScoreExtensions rule 5. `Catalog` frameworks are scored against a
// built-in requirement list — weighted, identifiers validated, exact. `OfficialCount` frameworks
// publish how many requirements they have but no list, so they are scored against that count,
// unweighted and unvalidated, which makes the figure a ceiling. `Unscorable` frameworks publish
// neither, so no percentage is calculated for them at all.

// True when the framework has no published requirement count, so no percentage may be shown for it.
export const isUnscorable = (framework) => framework?.readinessBasis === 'Unscorable';

// The readiness figure as it may be displayed: a percentage, or the words that replace one when
// there is no denominator. A published-count figure shows the plain percentage; that it is an
// estimate is said in the evidence sentence beneath it, not appended to the number.
export function formatReadiness(framework) {
  return isUnscorable(framework) ? 'Not scored' : `${framework?.adjustedCoveragePercent}%`;
}

// An unrecognised or missing basis states the counts and names no denominator. This matters: the
// first version of this wording keyed off a boolean served by the report endpoint while the modal
// reads the summary endpoint. It arrived `undefined`, fell through to the falsy branch, and told
// every customer the indicator was "computed over those only · 52 not assessed (excluded from the
// indicator)" while it was in fact measured against all 110. A missing signal must degrade to a
// claim that is merely incomplete, never to the opposite claim.
export function buildEvidenceLine({
  totalObservedControls,
  canonicalControlCount,
  observabilityPercent,
  notAssessed,
  readinessBasis,
  noun = 'controls',
}) {
  const observed = totalObservedControls ?? 0;
  const total = canonicalControlCount ?? 0;
  const pct = Math.round(observabilityPercent ?? 0);
  const missing = notAssessed ?? 0;
  // SOC 2 calls its items criteria; every other framework's noun is "controls" here.
  const head = `${observed} of ${total} ${noun} have evidence (${pct}% observability).`;

  // No published count to divide by: the line reports what evidence exists and says why there is
  // no percentage, rather than naming a denominator that does not exist.
  if (readinessBasis === 'Unscorable') {
    return `${observed} ${noun} have evidence. This framework publishes no official control count, `
      + 'so no readiness percentage is calculated.';
  }
  // Scored against the published count rather than a per-control mapping, so the figure is an
  // estimate. Stated as what it is based on — no need to narrate what Axaris has or has not built.
  if (readinessBasis === 'OfficialCount') {
    return `${head} The indicator is measured against all ${total}, based on the framework's `
      + 'published control count — treat the figure as an estimate.';
  }
  if (readinessBasis === 'Catalog') {
    return missing > 0
      ? `${head} The indicator is measured against all ${total}, so the ${missing} with no evidence count as not yet demonstrated and hold it down.`
      : `${head} The indicator is measured against all ${total}.`;
  }
  return head;
}

// ── Applicability ─────────────────────────────────────────────────────────────────────────────
// A framework whose requirements depend on what the organisation operates (today: NERC CIP) is
// scored only against the requirements that apply to it. The summary row sends the scoped count,
// the count it was cut from, and each gate that cut it with what it removed; this only words it.
// The server decides the exclusions; nothing here recomputes them.

// Why each gate removes its requirements, in the words of the applicability answer that did it.
const APPLICABILITY_REASONS = {
  HighMediumImpact: 'no High or Medium impact BES Cyber Systems under CIP-002',
  ControlCenter: 'no Control Center exchanging real-time data with another Control Center',
  TransmissionOwner: 'not a Transmission Owner subject to CIP-014',
  CriticalFacility: 'no critical facility identified and verified under CIP-014',
};

// An unrecognised gate still names what was excluded, and says only that the answers did it.
const FALLBACK_APPLICABILITY_REASON = 'does not apply, from the applicability answers';

// ["A"] → "A"; ["A", "B"] → "A and B"; ["A", "B", "C"] → "A, B and C".
const joinList = (items) => (items.length < 2
  ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

// "122 of 140 requirements apply. CIP-014 excluded: not a Transmission Owner subject to CIP-014."
// Null — render nothing — unless the server reports a scoped count below the catalog count and
// at least one gate that explains it.
export function buildApplicabilityLine({ scopedControlCount, catalogControlCount, excludedApplicability } = {}) {
  if (!Number.isFinite(scopedControlCount) || !Number.isFinite(catalogControlCount)) return null;
  if (scopedControlCount >= catalogControlCount) return null;
  const excluded = Array.isArray(excludedApplicability)
    ? excludedApplicability.filter((x) => Array.isArray(x?.scope) && x.scope.length > 0)
    : [];
  if (!excluded.length) return null;
  const reasons = excluded.map((x) => {
    const reason = APPLICABILITY_REASONS[x.gate] ?? FALLBACK_APPLICABILITY_REASON;
    return `${joinList(x.scope)} excluded: ${reason}.`;
  });
  return `${scopedControlCount} of ${catalogControlCount} requirements apply. ${reasons.join(' ')}`;
}

// ── Future readiness ──────────────────────────────────────────────────────────────────────────
// Requirements that are approved but not yet enforceable (today: NERC CIP-015) arrive on the
// summary row as `futureEffectiveControls`. They are reported beside the readiness figure, never
// in it. Their compliance is phased, so the window is always given as a range: no single date may
// be presented as when the standard "becomes enforceable" for the reader.

export const FUTURE_READINESS_HEADING = 'Future readiness';

export const FUTURE_READINESS_EXPLANATION = 'These requirements are approved but not yet enforceable, '
  + 'so they are shown for planning and are not included in the readiness percentage.';

// A framework whose only evidence is for future-effective requirements (today: an assessment that
// answered only the CIP-015 question) has no current readiness figure at all: nothing currently
// enforceable was observed. It is still listed, so its future readiness can be opened.
export function futureEffectiveControlsOf(framework) {
  return Array.isArray(framework?.futureEffectiveControls) ? framework.futureEffectiveControls : [];
}

export function isFutureReadinessOnly(framework) {
  return !(framework?.totalObservedControls > 0) && futureEffectiveControlsOf(framework).length > 0;
}

export const FUTURE_ONLY_READINESS_TEXT = 'No current score';

export const FUTURE_ONLY_EXPLANATION = 'None of the requirements that are enforceable today has '
  + 'evidence yet, so there is no current readiness score. The requirements below are approved but '
  + 'not yet enforceable and are shown for planning.';

const FUTURE_STATUS_LABELS = {
  pass: 'Pass',
  partial: 'Partial',
  fail: 'Fail',
  notassessed: 'Not Assessed',
};

// The server sends "Pass" / "Partial" / "Fail" / "Not Assessed". Anything else, including a
// missing status, reads as Not Assessed: an unknown status must never read as a pass.
export function futureStatusLabel(status) {
  const key = String(status ?? '').replace(/\s+/g, '').toLowerCase();
  return FUTURE_STATUS_LABELS[key] ?? 'Not Assessed';
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

// A `yyyy-MM-dd` date as "1 October 2028". Parsed by hand rather than through Date, so the
// reader's time zone can never move it to the previous day. Null for anything that is not a date.
export function formatComplianceDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!match) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${day} ${MONTH_NAMES[month - 1]} ${match[1]}`;
}

// The compliance window of one entry. `effectiveDate` is the EARLIEST phased date, not the date
// the requirement binds this reader, so it is only ever shown as the start of a range — or, when
// the server sends no latest date, with an explicit note that the reader's own date may be later.
export function complianceWindowText({ effectiveDate, latestComplianceDate } = {}) {
  const earliest = formatComplianceDate(effectiveDate);
  const latest = formatComplianceDate(latestComplianceDate);
  if (earliest && latest && earliest !== latest) {
    return `compliance phased from ${earliest} to ${latest}`;
  }
  if (earliest) {
    return `compliance phased in, earliest date ${earliest}; your date may be later`;
  }
  return null;
}

export function supersededByText(supersededBy) {
  return supersededBy ? `An approved successor standard exists: ${supersededBy}.` : null;
}

// Groups entries under their standard, in the order the server sent them. Generic on purpose: any
// framework that later reports future-effective controls renders the same way.
export function groupFutureEffectiveControls(controls) {
  if (!Array.isArray(controls)) return [];
  const groups = [];
  const byStandard = new Map();
  controls.forEach((control) => {
    const standard = control?.standard || 'Other';
    if (!byStandard.has(standard)) {
      const group = { standard, supersededBy: null, controls: [] };
      byStandard.set(standard, group);
      groups.push(group);
    }
    const group = byStandard.get(standard);
    group.supersededBy = group.supersededBy || control?.supersededBy || null;
    group.controls.push(control);
  });
  return groups;
}
